import { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import { useAdminListarComandasSischefQuery } from '../../../api';
import { baixarEtiquetasComandaZip } from '../../../lib/exportar-qrcodes';

/**
 * Etiquetas das comandas para a gráfica, geradas a partir das comandas
 * cadastradas no SISCHEF.
 *
 * O lojista ESCOLHE na lista real, não digita uma faixa. A faixa arbitrária
 * pedia que ele soubesse de cor onde começa e onde termina a numeração — e
 * errar para menos gera etiqueta faltando, que só aparece no dia em que um
 * cliente pega a comanda sem adesivo. A lista vem do cadastro, então o que está
 * na tela é exatamente o que existe no salão.
 *
 * Antes daqui o lojista tinha que recadastrar comanda por comanda como ponto de
 * acesso do cardápio, em outra tela — e é por isso que praticamente ninguém
 * usava. As comandas já existem no Sischef; a etiqueta sai delas.
 *
 * Fica em acordeão fechado: é tarefa de implantação, feita uma vez e revisitada
 * raramente. Aberta por padrão, ela empurrava para baixo as configurações do
 * tablet que o lojista mexe toda semana.
 *
 * O código impresso é só o número da comanda, sem link: ler com o celular não
 * abre o cardápio. As etiquetas assinadas antigas (`SCF1.…`) continuam valendo.
 */
export function EtiquetasDaComanda({ idConfig }: { idConfig: number }): JSX.Element {
  const [aberto, setAberto] = useState(false);
  // Faixa só entra em cena quando o cadastro passa do teto da chamada — ver o
  // bloco de `truncado` lá embaixo. Aplicada por botão, não a cada tecla.
  const [faixa, setFaixa] = useState({ de: '', ate: '' });
  const [faixaAplicada, setFaixaAplicada] = useState({ de: '', ate: '' });
  const [busca, setBusca] = useState('');
  const [selecionadas, setSelecionadas] = useState<Set<string>>(new Set());
  const [progresso, setProgresso] = useState<{ feitas: number; total: number } | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const { data, isFetching, isError, refetch } = useAdminListarComandasSischefQuery(
    { idConfig, de: faixaAplicada.de, ate: faixaAplicada.ate },
    // Só busca quando o acordeão abre: numa loja grande são 500 comandas e uma
    // assinatura HMAC por comanda, à toa em toda visita à tela de configuração.
    { skip: !aberto },
  );

  const comandas = useMemo(() => data?.comandas ?? [], [data]);

  // Chegou lista nova: tudo marcado. O caso comum é imprimir todas — obrigar a
  // marcar 200 chips uma a uma para fazer o óbvio seria trabalho inventado.
  useEffect(() => {
    setSelecionadas(new Set(comandas.map((c) => c.numero)));
  }, [comandas]);

  const visiveis = useMemo(() => {
    const alvo = busca.trim();
    if (!alvo) return comandas;
    return comandas.filter((c) => c.numero.includes(alvo));
  }, [comandas, busca]);

  function alternar(numero: string): void {
    setSelecionadas((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(numero)) proximo.delete(numero);
      else proximo.add(numero);
      return proximo;
    });
  }

  /** Marca/desmarca o que está VISÍVEL — com filtro ativo, agir no que está
   *  escondido seria mexer no que o lojista não está vendo. */
  function marcarVisiveis(marcar: boolean): void {
    setSelecionadas((atual) => {
      const proximo = new Set(atual);
      for (const c of visiveis) {
        if (marcar) proximo.add(c.numero);
        else proximo.delete(c.numero);
      }
      return proximo;
    });
  }

  async function baixar(): Promise<void> {
    setErro(null);
    setAviso(null);

    const escolhidas = comandas.filter((c) => selecionadas.has(c.numero));
    if (escolhidas.length === 0) {
      setErro('Selecione ao menos uma comanda.');
      return;
    }

    setProgresso({ feitas: 0, total: escolhidas.length });
    try {
      const quantas = await baixarEtiquetasComandaZip(escolhidas, 'etiquetas-comandas.zip', (feitas, total) =>
        setProgresso({ feitas, total }),
      );
      setAviso(`${quantas} ${quantas === 1 ? 'etiqueta baixada' : 'etiquetas baixadas'}.`);
    } catch {
      setErro('Não foi possível gerar o arquivo. Tente com menos comandas de cada vez.');
    } finally {
      setProgresso(null);
    }
  }

  const total = comandas.length;
  const marcadas = selecionadas.size;

  return (
    <Accordion
      disableGutters
      expanded={aberto}
      onChange={(_e, exp) => setAberto(exp)}
      sx={{ mt: 2, '&::before': { display: 'none' }, border: 1, borderColor: 'divider', borderRadius: 1 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
          <QrCode2Icon color="action" />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600 }}>Como gerar os QR Codes das comandas?</Typography>
            <Typography variant="caption" color="text.secondary">
              As etiquetas saem das comandas já cadastradas no Sischef.
            </Typography>
          </Box>
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 0 }}>
        <Stack spacing={2}>
          <Box component="ol" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.75 } }}>
            <Typography component="li" variant="body2">
              <strong>Escolha as comandas</strong> na lista abaixo. Ela vem do cadastro do Sischef — não
              precisa recadastrar nada aqui.
            </Typography>
            <Typography component="li" variant="body2">
              <strong>Baixe o ZIP</strong> e mande imprimir. Cada arquivo é um PNG com o QR e o número da
              comanda por extenso, para a gráfica e para você conferir na hora de colar.
            </Typography>
            <Typography component="li" variant="body2">
              <strong>Cole a etiqueta na comanda física.</strong> O código é fixo e vale enquanto aquela
              comanda existir no Sischef; inativá-la lá é o que revoga a etiqueta.
            </Typography>
            <Typography component="li" variant="body2">
              <strong>No tablet</strong>, o cliente aponta a etiqueta para a câmera e a comanda entra
              sozinha, sem digitar.
            </Typography>
          </Box>

          <Alert severity="info" variant="outlined">
            O código impresso é <strong>só o número da comanda</strong>, sem link: quem apontar o
            celular não abre o cardápio. Etiquetas impressas antes desta versão continuam funcionando.
          </Alert>

          <Divider />

          {isFetching ? (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Buscando as comandas cadastradas no Sischef…
              </Typography>
            </Stack>
          ) : isError ? (
            <Alert
              severity="warning"
              action={
                <Button size="small" color="inherit" onClick={() => void refetch()}>
                  Tentar de novo
                </Button>
              }
            >
              Não foi possível buscar as comandas do Sischef.
            </Alert>
          ) : total === 0 ? (
            <Alert severity="warning">
              Nenhuma comanda ativa encontrada no Sischef
              {faixaAplicada.de || faixaAplicada.ate ? ' para esta faixa' : ''}. Cadastre as comandas no
              Sischef (ou reative as que estão inativas) e volte aqui — a etiqueta sai de lá.
            </Alert>
          ) : (
            <>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', sm: 'center' }}
              >
                <TextField
                  size="small"
                  placeholder="Filtrar por número"
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  sx={{ width: { xs: '100%', sm: 200 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button size="small" onClick={() => marcarVisiveis(true)}>
                  {busca.trim() ? `Marcar as ${visiveis.length}` : 'Marcar todas'}
                </Button>
                <Button size="small" color="inherit" onClick={() => marcarVisiveis(false)}>
                  Desmarcar
                </Button>

                <Box sx={{ flexGrow: 1 }} />

                <Typography variant="caption" color="text.secondary">
                  {marcadas} de {total} selecionadas
                </Typography>
              </Stack>

              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0.75,
                  maxHeight: 220,
                  overflowY: 'auto',
                  p: 1,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                {visiveis.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                    Nenhuma comanda com esse número.
                  </Typography>
                ) : (
                  visiveis.map((c) => {
                    const marcada = selecionadas.has(c.numero);
                    return (
                      <Chip
                        key={c.numero}
                        label={c.numero}
                        size="small"
                        onClick={() => alternar(c.numero)}
                        color={marcada ? 'primary' : 'default'}
                        variant={marcada ? 'filled' : 'outlined'}
                      />
                    );
                  })
                )}
              </Box>

              {/* A faixa só aparece quando o cadastro passa do teto da chamada:
                  é escape, não o caminho principal. */}
              {data?.truncado && (
                <Alert severity="info">
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Esta loja tem mais de {data.limite} comandas cadastradas e a lista foi cortada. Informe
                    uma faixa para alcançar o restante — o ZIP é montado aqui no navegador e não aguenta
                    tudo de uma vez.
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <TextField
                      size="small"
                      label="Da comanda"
                      value={faixa.de}
                      onChange={(e) => setFaixa((f) => ({ ...f, de: e.target.value }))}
                      sx={{ width: 120 }}
                      inputProps={{ inputMode: 'numeric' }}
                    />
                    <TextField
                      size="small"
                      label="Até"
                      value={faixa.ate}
                      onChange={(e) => setFaixa((f) => ({ ...f, ate: e.target.value }))}
                      sx={{ width: 120 }}
                      inputProps={{ inputMode: 'numeric' }}
                    />
                    <Button size="small" variant="outlined" onClick={() => setFaixaAplicada(faixa)}>
                      Aplicar faixa
                    </Button>
                  </Stack>
                </Alert>
              )}

              <Box>
                <Button
                  variant="contained"
                  startIcon={
                    progresso ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />
                  }
                  disabled={!!progresso || marcadas === 0}
                  onClick={() => void baixar()}
                >
                  {progresso
                    ? `Gerando ${progresso.feitas} de ${progresso.total}…`
                    : `Baixar ${marcadas} ${marcadas === 1 ? 'etiqueta' : 'etiquetas'} (ZIP)`}
                </Button>
              </Box>
            </>
          )}

          {erro && <Alert severity="warning">{erro}</Alert>}
          {aviso && <Alert severity="success">{aviso}</Alert>}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
